import {ArrowDownRight, ArrowUpRight, Minus} from "lucide-react";

import {Badge} from "@/components/ui/badge";
import {cn} from "@/lib/utils";

import {formatDeltaPct} from "../lib";

type DeltaPillProps = {
  value: number | null | undefined;
  /**
   * For metrics where lower is better (e.g. missed shifts), invert the
   * positive/negative color mapping so a drop reads as success.
   */
  invertGoodBad?: boolean;
  className?: string;
  size?: "default" | "sm";
};

/**
 * Compact delta indicator used in KPI cards. Conveys direction with both
 * an arrow and color so meaning is not color-only.
 */
export function DeltaPill({
  value,
  invertGoodBad = false,
  className,
  size = "default",
}: DeltaPillProps) {
  const sm = size === "sm";
  const badgePad = sm ? "h-5 gap-0.5 px-1.5 py-0 text-[10px]" : "gap-1 font-medium";
  const iconCls = sm ? "size-2.5" : "size-3";

  if (value === null || value === undefined || Number.isNaN(value)) {
    return (
      <Badge variant="secondary" className={cn(badgePad, className)}>
        <Minus className={iconCls} />
        <span className="tabular-nums">—</span>
      </Badge>
    );
  }

  const isUp = value > 0;
  const isFlat = value === 0;
  const isPositiveSentiment = invertGoodBad ? !isUp : isUp;

  const colorClass = isFlat
    ? "border-transparent bg-muted text-muted-foreground"
    : isPositiveSentiment
      ? "border-primary/20 bg-primary/10 text-primary"
      : "border-destructive/20 bg-destructive/10 text-destructive";

  const Icon = isFlat ? Minus : isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <Badge variant="secondary" className={cn(badgePad, "border", colorClass, className)}>
      <Icon className={iconCls} />
      <span className="tabular-nums">{formatDeltaPct(value)}</span>
    </Badge>
  );
}
